import './index.css'
import { bindActionCreators } from '@reduxjs/toolkit'
import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { NavigateFunction } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { TRACK_SHARING } from '@disco/constants/FeatureTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  resetPlaylistShareForm,
  updatePlaylistShareForm,
} from '@disco/state-management/actions/FormActions'
import { editContactField } from '@disco/state-management/actions/PlaylistShareActions'
import {
  getTrackShares,
  trackShare,
} from '@disco/state-management/actions/TrackShareActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { trackSharesWithContacts } from '@disco/state-management/selectors/shares'
import {
  getBusinessEmailTemplates,
  getUser,
} from '@disco/state-management/selectors/user'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { GroupId } from 'types/group'
import type { Track } from 'types/track'
import type { User } from 'types/user'
import type { RootState } from 'store/configureStore'

import Row from 'components/common/row'
import TrackInfo from 'components/common/track-info'
import EmbedTab from 'components/modals/playlist/share/embed-tab'
import { Button, Tab, Tabs } from 'components/ui'
import { Box, Flex } from 'components/ds'
import Alert from 'components/ui/alert'
import CopyUrlItem from 'components/ui/copy-url-item'
import Heading from 'components/ui/heading'
import { HoverProvider } from 'components/ui/hover-provider'
import ModalWindow from 'components/ui/modal-window'

import TrackShareTab from './track-share-tab'

const mapStateToProps = (state: RootState, ownProps: { track: Track }) => {
  const { track, sharesLoaded, sharesItems } = trackSharesWithContacts(state, {
    id: ownProps.track.id,
  })

  return {
    disablePublicDownloads:
      state.user.business?.private_metadata.disable_public_downloads,
    isPhone: isDeviceTypePhone(state),
    sharesLoaded,
    sharesItems,
    track,
    emailTemplate: getBusinessEmailTemplates(state),
    user: getUser(state),
    isTrackSharingEnabled: isFeatureEnabled(state, {
      featureName: TRACK_SHARING,
    }),
    ...state.forms.playlistShareForm,
  }
}

const mapDispatchToProps = {
  resetPlaylistShareForm,
  updatePlaylistShareForm,
  editContactField,
  sendMixpanelEvent,
  sendGenericError,
  trackShare,
  getTrackShares,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  onClose: (...args: Array<any>) => any
  sharedChannel?: boolean
  navigate: NavigateFunction
}

class Share extends Component<
  Props,
  {
    tabIndex: number
    downloadUrl: string
    playUrl: string
    streamUrl: string
    embedParams: {
      download: boolean
    }
    embedUrl: string
    disabledShares: Record<number, boolean>
  }
> {
  constructor(props: Props) {
    super(props)
    const { track } = this.props
    this.state = {
      tabIndex: 0,
      downloadUrl: track.public_download_url,
      playUrl: track.public_play_url,
      streamUrl: track.stream_url,
      embedParams: {
        download: !props.disablePublicDownloads,
      },
      embedUrl: '',
      disabledShares: {},
    }
    this._onShareHandle = this._onShareHandle.bind(this)
  }

  componentDidMount() {
    this.fetchTrackAndUrls()
    this._fetchEmbeds(this.props, this.state.embedParams)

    // Properties for event #5 from https://disco-team.atlassian.net/browse/DISCO-5985

    const context = {
      Modal: 'Share Track',
      'Track ID': this.props.track.id,
      Catalog: this.props.track.origin?.source_library?.id,
      'Catalog Business': this.props.track.origin?.business?.id,
      ...this._addSharedChannelContextIfRelevant(),
    }

    mixpanel.sendPureEvent('Open modal', context)
  }

  _onShareHandle(shareId: number) {
    const path = `/explorer/tracks/${this.props.track.id}/shared/${shareId}`
    this.props.navigate(path)
  }

  async fetchTrackAndUrls() {
    this.props.getTrackShares(this.props.track.id)
    const { track } = this.props

    try {
      const [downloadUrl, playUrl, streamUrl] = await api.shorturl([
        track.public_download_url,
        track.public_play_url,
        track.stream_url,
      ])
      this.setState({
        downloadUrl: downloadUrl ?? '',
        playUrl: playUrl ?? '',
        streamUrl: streamUrl ?? '',
      })
    } catch (error) {
      this.props.sendGenericError(error)
    }
  }

  async _fetchEmbeds({ track }: any, params: any) {
    const embedUrl = await api.getEmbeddedTrackUrl(track.id, { ...params })
    this.setState({
      embedUrl,
    })
  }

  _addSharedChannelContextIfRelevant = () => {
    if (this.props.sharedChannel) {
      return {
        Context: 'Shared Channels',
      }
    }

    return {}
  }

  _handleTabChange = (tabIndex: number) =>
    this.setState({
      tabIndex,
    })
  _onDownloadUrlCopy = () =>
    mixpanel.sendPureEvent('Copy track download url', {
      Url: this.state.downloadUrl,
      ...this._addSharedChannelContextIfRelevant(),
    })
  _onPlayUrlCopy = () =>
    mixpanel.sendPureEvent('Copy track url', {
      Url: this.state.playUrl,
      // Properties for event #7 from https://disco-team.atlassian.net/browse/DISCO-5985
      Modal: 'Share Track',
      'Track ID': this.props.track.id,
      Catalog: this.props.track.origin?.source_library?.id,
      'Catalog Business': this.props.track.origin?.business?.id,
      ...this._addSharedChannelContextIfRelevant(),
    })
  _onStreamUrlCopy = () =>
    mixpanel.sendPureEvent('Copy stream url', {
      Url: this.state.streamUrl,
      ...this._addSharedChannelContextIfRelevant(),
    })
  _onPlayUrlQrAction = (action: 'download' | 'copy') => {
    mixpanel.sendPureEvent('Copy track url', {
      Url: this.state.playUrl,
      Modal: 'Share Track',
      'Track ID': this.props.track.id,
      Catalog: this.props.track.origin?.source_library?.id,
      'Catalog Business': this.props.track.origin?.business?.id,
      Via: 'QR',
      Action: action,
      ...this._addSharedChannelContextIfRelevant(),
    })
  }
  _onStreamUrlQrAction = (action: 'download' | 'copy') => {
    mixpanel.sendPureEvent('Copy stream url', {
      Url: this.state.streamUrl,
      Via: 'QR',
      Action: action,
      ...this._addSharedChannelContextIfRelevant(),
    })
  }
  _handleEmbedParamsChange = (params: any) => {
    this.setState(
      {
        embedParams: { ...this.state.embedParams, ...params },
      },
      () => this._fetchEmbeds(this.props, this.state.embedParams),
    )
  }

  _handleCopyTrackUrl = () => {
    const { playUrl } = this.state
    if (playUrl) {
      navigator.clipboard
        .writeText(playUrl)
        .then(() => {
          pushUINotification({
            level: 'success',
            message: 'Copied track link to clipboard',
          })
          this.props.sendMixpanelEvent('Track Share Modal - Copy track link')
        })
        .catch(() => {
          pushUINotification({
            level: 'error',
            message: 'Failed to copy link to clipboard',
          })
        })
    }
  }
  componentWillUnmount() {
    this.props.resetPlaylistShareForm()
  }

  render() {
    const { track } = this.props
    if (!track) return null
    const fileType = getFileTypeByExt(track.ext)
    const displayEmbedTab = fileType === AUDIO_FILE || fileType === VIDEO_FILE
    const hasNestedTracks = getNestedTracksCount(track) > 0
    return (
      <ModalWindow
        header={<Heading size="700">Share Track</Heading>}
        isOpen
        width="medium"
        onClose={this.props.onClose}
      >
        {!this.props.isPhone && (
          <div className="TrackShareDialog_TrackInfoContainer">
            <Flex align="center" gap="2" width="100%">
              <Flex align="center" gap="2" flexGrow="1" minWidth="0">
                <Box minWidth="0">
                  <HoverProvider>
                    <TrackInfo location="explorer" track={track} />
                  </HoverProvider>
                </Box>
              </Flex>
              <Button
                appearance="icon"
                icon="link"
                onClick={this._handleCopyTrackUrl}
                tooltip="Copy public link"
              />
              <Button
                appearance="icon"
                icon="open-in-new-tab"
                link={this.state.playUrl}
                linkTarget="_blank"
                tooltip="Open public link in new tab"
              />
            </Flex>
          </div>
        )}
        <Tabs
          className="Tabs--modal"
          index={this.state.tabIndex}
          onChange={this._handleTabChange}
        >
          {this.props.isTrackSharingEnabled && (
            <Tab label="Share to contact">
              <TrackShareTab
                onClose={this.props.onClose}
                onShare={this._onShareHandle}
                subject={this.props.subject}
                message={this.props.message}
                territories={this.props.territories}
                shareFunction={this.props.trackShare}
                emailTemplates={this.props.emailTemplate}
                user={this.props.user}
                recipients={this.props.recipients}
                shareObject={track}
                sharesItems={this.props.sharesItems}
                sharesLoaded={this.props.sharesLoaded}
                sharing={this.props.sharing}
                longTabNames={!this.props.isPhone}
                navigate={this.props.navigate}
                sendMixpanelEvent={this.props.sendMixpanelEvent}
                pushUINotification={pushUINotification}
                editContactField={this.props.editContactField}
                updatePlaylistShareForm={this.props.updatePlaylistShareForm}
                disabledShares={this.state.disabledShares}
                setShareDisabled={(recipient, val) => {
                  this.setState({
                    disabledShares: {
                      ...this.state.disabledShares,
                      [recipient.id]: val,
                    },
                  })
                }}
              />
            </Tab>
          )}
          <Tab label="Copy URL">
            {track.watermark && (
              <Row columnsSize={[12]}>
                <div>
                  <Alert status="warning" icon="warning">
                    Watermarked tracks downloaded from public track share links
                    will not be traceable to individual recipients.
                  </Alert>
                </div>
              </Row>
            )}
            {hasNestedTracks && (
              <Row columnsSize={[12]}>
                <div>
                  <Alert
                    status="change"
                    icon="info"
                    dismissible
                    id="share_nested_track"
                  >
                    You’re sharing a track with nested versions. All versions
                    will be visible on this link.
                  </Alert>
                </div>
              </Row>
            )}
            {!this.props.disablePublicDownloads && (
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <CopyUrlItem
                    label="Track URL"
                    url={this.state.playUrl}
                    onCopy={this._onPlayUrlCopy}
                    enableQR
                    qrFilename={`${this.props.track.name || 'track'}-qr`}
                    onQrAction={this._onPlayUrlQrAction}
                  />
                </div>
              </Row>
            )}
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <CopyUrlItem
                  label="Streaming-only URL"
                  url={this.state.streamUrl}
                  onCopy={this._onStreamUrlCopy}
                  enableQR
                  qrFilename={`${this.props.track.name || 'track'}-stream-qr`}
                  onQrAction={this._onStreamUrlQrAction}
                />
              </div>
            </Row>
          </Tab>
          {displayEmbedTab && (
            <Tab label="Embed">
              {track.watermark && (
                <div className="shareEmbed__alertContainer">
                  <Alert status="warning" icon="warning">
                    Watermarked tracks downloaded from embedded players will not
                    be traceable to individual recipients.
                  </Alert>
                </div>
              )}
              {hasNestedTracks && (
                <Row columnsSize={[12]}>
                  <div>
                    <Alert
                      status="change"
                      icon="info"
                      dismissible
                      id="embed_nested_track"
                    >
                      Only the main version of this track will appear in your
                      embed.
                    </Alert>
                  </div>
                </Row>
              )}
              <EmbedTab
                iframeId={`disco-track-${this.props.track.id}`}
                embedUrl={this.state.embedUrl}
                embedParams={this.state.embedParams}
                onParamsChange={this._handleEmbedParamsChange}
                onClose={this.props.onClose}
                type="track"
                disablePublicDownloads={Boolean(
                  this.props.disablePublicDownloads,
                )}
              />
            </Tab>
          )}
        </Tabs>
      </ModalWindow>
    )
  }
}

const ConnectedShare = connector(Share)

const ShareWithNavigation = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedShare {...props} navigate={navigate} />
}

export default ShareWithNavigation
